import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { darken, lighten } from '@material-ui/core/styles/colorManipulator'
import Switch from '@material-ui/core/Switch'
import { CHOICE_ID_OK } from '@purecarslabs/signal-components/ModalPrompt'
/* webpack-strip-block:removed */
import isDarkColor from '@purecarslabs/signal-utils/isDarkColor'
import renderIf from '@purecarslabs/signal-utils/renderIf'
import { EXTRA_SUBSCRIBE_TO_PRICE_DROPS } from '@app-src/containers/ApiContainer/constants'
import FormContent from '@app-src/containers/FormContent'
import { NativeLeadType } from '@app-src/data/leads/types.ts'
import { debugLogForms } from '@app-src/debug'
import { FormTriggerPropsType } from '@app-src/types'
import {
  EmbedWrapper,
  PriceDropTypography,
  PriceDropWrapper,
  SubmissionCompleteTypography,
  SubmissionCompleteWrapper,
  SubmitButton,
  SubmitWrapper
} from './styles'

type EmbedTriggerPropsType = {
  submissionCompleteTest?: boolean
} & FormTriggerPropsType

// this is the form trigger component, which handles owning the modal which is generated containing
// the form
export const EmbedTrigger: React.FC<EmbedTriggerPropsType> = ({
  activeFormDetails,
  formLoaded,
  extraFlags,
  mvhColors,
  onAfterSubmitLead,
  setExtraFlags,
  submissionCompleteTest = false,
  ...rest
}) => {
  // hooks, state, and refs
  /////////////////////////

  const [formEverLoaded, setFormEverLoaded] = useState<boolean>(false)
  const [submitEnabled, setSubmitEnabled] = useState<boolean>(true)
  const [submissionComplete, setSubmissionComplete] = useState<boolean>(
    submissionCompleteTest
  )
  const submitFormikRef = useRef<((evt: React.FormEvent) => void) | undefined>()
  const embedRef = useRef<HTMLDivElement>(null)

  // memos
  ////////

  const includePriceDrops = Boolean(extraFlags & EXTRA_SUBSCRIBE_TO_PRICE_DROPS)

  const embedColors = useMemo(() => {
    let submitButtonColor = mvhColors?.submitButtonColor
    let submitButtonTextColor = mvhColors?.submitButtonTextColor
    if (submitButtonColor) {
      try {
        submitButtonColor = darken(submitButtonColor, 0.3)
        if (!isDarkColor(submitButtonColor)) {
          submitButtonColor = darken(submitButtonColor, 0.3)
        }
        submitButtonTextColor = submitButtonTextColor
          ? lighten(submitButtonTextColor, 0.9)
          : '#fff'
      } catch (e) {
        submitButtonColor = '#000'
        submitButtonTextColor = '#fff'
      }
    }
    return {
      ...mvhColors,
      submitButtonColor,
      submitButtonTextColor
    }
  }, [mvhColors])

  const isLightTextColor = useMemo(() => {
    try {
      return mvhColors?.textColor ? !isDarkColor(mvhColors.textColor) : false
    } catch (e) {
      return true
    }
  }, [mvhColors])

  // effects
  //////////

  useEffect(() => {
    setExtraFlags(
      Boolean(activeFormDetails.leadContext?.vin)
        ? EXTRA_SUBSCRIBE_TO_PRICE_DROPS
        : 0
    )
  }, [activeFormDetails.leadContext?.vin, setExtraFlags])

  useEffect(() => {
    if (formLoaded) {
      setFormEverLoaded(true)
    }
  }, [formLoaded, setFormEverLoaded])

  // callbacks
  ////////////

  const handleAfterSubmitLead = useCallback(
    (lead: NativeLeadType) => {
      // set a defined height so our closing animation will work
      const form = embedRef.current?.querySelector('form')
      if (form) {
        form.style.height = window.getComputedStyle(form).height
      }

      onAfterSubmitLead(lead)

      setTimeout(() => {
        setSubmissionComplete(true)
      }, 100) // give time for browser to render with defined height for closing animation

      submitFormikRef.current = undefined
    },
    [onAfterSubmitLead, setSubmissionComplete]
  )

  const handleNeedEnableChoice = useCallback(
    () => (choiceId: string, enabled: boolean) => {
      if (choiceId === CHOICE_ID_OK) {
        setSubmitEnabled(enabled)
      }
    },
    [setSubmitEnabled]
  )

  const handlePriceDropSwitchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setExtraFlags(
        e.currentTarget.checked ? EXTRA_SUBSCRIBE_TO_PRICE_DROPS : 0
      )
    },
    [setExtraFlags]
  )

  const handleSubmit = useCallback(() => {
    debugLogForms('saw embed form submit')

    // if not cancelling and we still have a formik ref, then submit the form via formik
    if (submitFormikRef.current) {
      // @ts-ignore
      submitFormikRef.current()
    }
  }, [submitFormikRef])

  return (
    <EmbedWrapper
      /* if-not-on-site-begin */
      data-testid="embed-wrapper"
      /* if-not-on-site-end */
      ref={embedRef}
      $mvhColors={embedColors}
      $isLightTextColor={isLightTextColor}
      $submissionComplete={submissionComplete}
    >
      <FormContent
        isEmbedded
        {...activeFormDetails}
        mvhColors={mvhColors}
        onNeedEnableChoice={handleNeedEnableChoice}
        onAfterSubmitLead={handleAfterSubmitLead}
        submitFormikRef={submitFormikRef}
        /* webpack-strip-block:removed */
        {...rest}
      />
      {renderIf(
        submissionComplete,
        () => (
          <SubmissionCompleteWrapper
            /* if-not-on-site-begin */
            data-testid="submission-complete"
            /* if-not-on-site-end */
          >
            <SubmissionCompleteTypography>
              Your request has been submitted successfully!
            </SubmissionCompleteTypography>
          </SubmissionCompleteWrapper>
        ),
        () => (
          <SubmitWrapper>
            {renderIf(formEverLoaded, () => (
              <>
                <SubmitButton
                  /* if-not-on-site-begin */
                  data-testid="submit-button"
                  /* if-not-on-site-end */
                  disabled={!submitEnabled || submissionComplete}
                  onClick={handleSubmit}
                >
                  Submit
                </SubmitButton>
                {renderIf(Boolean(activeFormDetails.leadContext?.vin), () => (
                  <PriceDropWrapper $includePriceDrops={includePriceDrops}>
                    <PriceDropTypography>
                      Notify me when the price drops on this vehicle
                    </PriceDropTypography>
                    <Switch
                      /* webpack-strip-block:removed */
                      disabled={!submitEnabled || submissionComplete}
                      checked={includePriceDrops}
                      onChange={handlePriceDropSwitchChange}
                    />
                  </PriceDropWrapper>
                ))}
              </>
            ))}
          </SubmitWrapper>
        )
      )}
    </EmbedWrapper>
  )
}

export default memo(EmbedTrigger)
